import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { 
  Cpu, 
  ShoppingCart, 
  FlaskConical, 
  CheckCircle2, 
  Rocket, 
  ArrowRight,
  Menu,
  X,
  Mail,
  ExternalLink,
  Sparkles,
  Zap,
  TrendingUp,
  BarChart3,
  Globe,
  Layers,
  ChevronDown,
  MousePointer2
} from 'lucide-react';

// Import pages and components
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Footer from './components/Footer';
import Navigation from './components/Navigation';

// Custom X Logo Component
const XLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="10" fill="url(#logoGradient)"/>
    <path 
      d="M12 12L20 20M20 20L28 28M20 20L28 12M20 20L12 28" 
      stroke="white" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="logoGradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#002699"/>
        <stop offset="1" stopColor="#E62020"/>
      </linearGradient>
    </defs>
  </svg>
);

// Animated Scroll Indicator with Rocket Trail Effect
const ScrollIndicator = () => (
  <a 
    href="#what-we-build" 
    className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-gray-400 hover:text-[#002699] transition-colors cursor-pointer group"
  >
    <span className="text-xs uppercase tracking-widest font-medium opacity-60 group-hover:opacity-100 transition-opacity">
      Scroll to explore
    </span>
    
    {/* Rocket Trail Animation Container */}
    <div className="relative">
      {/* Outer glow ring */}
      <div className="absolute inset-0 w-14 h-14 rounded-full bg-gradient-to-b from-[#002699]/20 to-[#E62020]/20 blur-md animate-ping-slow" />
      
      {/* Main circle with arrow */}
      <div className="relative w-14 h-14 rounded-full border-2 border-gray-200 group-hover:border-[#002699] bg-white/80 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:shadow-lg group-hover:shadow-[#002699]/20">
        {/* Animated arrow */}
        <div className="animate-scroll-arrow">
          <ChevronDown className="w-6 h-6" strokeWidth={2.5} />
        </div>
      </div>
      
      {/* Rocket trail particles */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="w-0.5 h-4 bg-gradient-to-b from-[#002699]/40 to-transparent animate-trail-1" />
        <div className="w-0.5 h-3 bg-gradient-to-b from-[#E62020]/30 to-transparent animate-trail-2 -mt-1" />
        <div className="w-0.5 h-2 bg-gradient-to-b from-[#002699]/20 to-transparent animate-trail-3 -mt-1" />
      </div>
      
      {/* Side particles */}
      <div className="absolute top-full left-1/2 -translate-x-1/2">
        <div className="absolute -left-2 top-1 w-1 h-1 rounded-full bg-[#002699]/40 animate-particle-left" />
        <div className="absolute -right-2 top-1 w-1 h-1 rounded-full bg-[#E62020]/40 animate-particle-right" />
        <div className="absolute -left-1 top-3 w-0.5 h-0.5 rounded-full bg-[#002699]/30 animate-particle-left-2" />
        <div className="absolute -right-1 top-3 w-0.5 h-0.5 rounded-full bg-[#E62020]/30 animate-particle-right-2" />
      </div>
    </div>
  </a>
);

// Animated Background Particles
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 rounded-full opacity-20"
        style={{
          background: i % 2 === 0 ? '#002699' : '#E62020',
          left: `${15 + i * 15}%`,
          top: `${20 + (i % 3) * 25}%`,
          animation: `float ${4 + i}s ease-in-out infinite`,
          animationDelay: `${i * 0.5}s`,
        }}
      />
    ))}
  </div>
);

// Hero Section
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center bg-white bg-grid overflow-hidden"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#002699]/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#E62020]/10 rounded-full blur-3xl animate-pulse-slow animation-delay-2000" />
      </div>
      
      <FloatingParticles />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-28 sm:pb-32">
        <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gray-50 border border-gray-200 mb-6 sm:mb-8 animate-fade-in-up animation-delay-200">
            <Sparkles className="w-4 h-4 text-[#002699] animate-pulse" />
            <span className="text-xs sm:text-sm text-gray-600">AI-Native Venture Studio</span>
          </div>

          {/* Main Headline */}
          <h1 
            className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight tracking-tight mb-6 sm:mb-8 text-gray-900 animate-fade-in-up animation-delay-400"
            data-testid="hero-headline"
          >
            An AI-native lab building{' '}
            <span className="gradient-text animate-gradient">autonomous digital businesses.</span>
          </h1>

          {/* Sub-headline */}
          <p 
            className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed animate-fade-in-up animation-delay-600 px-2"
            data-testid="hero-subheadline"
          >
            Xerobrain Labs creates AI-powered software, platforms, and commerce systems 
            that operate, learn, and scale with minimal human involvement.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-fade-in-up animation-delay-800">
            <a
              href="#what-we-build"
              className="btn-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium text-white flex items-center gap-2 group hover:scale-105 transition-all duration-300 w-full sm:w-auto justify-center"
              data-testid="explore-btn"
            >
              Explore What We&apos;re Building
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/contact"
              className="btn-secondary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:scale-105 transition-all duration-300 w-full sm:w-auto text-center"
              data-testid="contact-btn"
            >
              Contact
            </Link>
          </div>
        </div>

        {/* Scroll Down Indicator - Positioned lower */}
        <ScrollIndicator />
      </div>
    </section>
  );
};

// What We Build Section
const WhatWeBuildSection = () => {
  const cards = [
    {
      icon: Cpu,
      title: 'AI Systems',
      description: 'Autonomous agents, intelligent workflows, and decision-making software.',
      color: 'bg-[#002699]',
    },
    {
      icon: Layers,
      title: 'AI Products',
      description: 'SaaS platforms, trading systems, and tools powered by machine intelligence.',
      color: 'bg-gradient-to-br from-[#002699] to-[#E62020]',
    },
    {
      icon: ShoppingCart,
      title: 'AI-Driven Commerce',
      description: 'Content, e-commerce, and media businesses operated and optimized by AI.',
      color: 'bg-[#E62020]',
    },
  ];

  return (
    <section 
      id="what-we-build"
      data-testid="what-we-build-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
            What We <span className="gradient-text">Build</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto px-2">
            Building the next generation of intelligent, autonomous systems.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {cards.map((card, index) => (
            <div
              key={card.title}
              className="card-hover gradient-border rounded-xl sm:rounded-2xl p-6 sm:p-8 bg-white group"
              data-testid={`build-card-${index}`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className={`w-12 sm:w-14 h-12 sm:h-14 rounded-lg sm:rounded-xl ${card.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                <card.icon className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-900 group-hover:text-[#002699] transition-colors">{card.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{card.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-16 sm:mt-24 lg:mt-32" />
    </section>
  );
};

// Our Model Section
const OurModelSection = () => {
  const steps = [
    {
      icon: FlaskConical,
      title: 'Experiment',
      description: 'We rapidly prototype and test AI-driven product ideas.',
      number: '01',
      color: 'text-[#002699]',
      bgColor: 'bg-[#002699]/10',
      borderColor: 'border-[#002699]/30',
    },
    {
      icon: CheckCircle2,
      title: 'Validate',
      description: 'We use data, traffic, and automation to find what works.',
      number: '02',
      color: 'text-[#6B21A8]',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-300',
    },
    {
      icon: Rocket,
      title: 'Scale',
      description: 'Winning experiments are turned into full-scale digital businesses.',
      number: '03',
      color: 'text-[#E62020]',
      bgColor: 'bg-[#E62020]/10',
      borderColor: 'border-[#E62020]/30',
    },
  ];

  return (
    <section 
      id="our-model"
      data-testid="our-model-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-gray-50 bg-grid overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900 px-2">
            Built like a lab.{' '}
            <span className="gradient-text">Scaled like a startup.</span>
          </h2>
        </div>

        {/* Steps Grid */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-16">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="relative text-center group"
              data-testid={`model-step-${index}`}
            >
              {/* Number */}
              <div className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-100 absolute -top-4 sm:-top-6 lg:-top-8 left-1/2 -translate-x-1/2 group-hover:text-gray-200 transition-colors">
                {step.number}
              </div>
              
              {/* Icon */}
              <div className={`relative z-10 w-16 sm:w-20 h-16 sm:h-20 mx-auto rounded-xl sm:rounded-2xl ${step.bgColor} border ${step.borderColor} flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-300`}>
                <step.icon className={`w-8 sm:w-10 h-8 sm:h-10 ${step.color}`} />
              </div>

              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-900">{step.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-xs mx-auto">
                {step.description}
              </p>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-10 left-[calc(50%+60px)] w-[calc(100%-120px)] h-px bg-gradient-to-r from-[#002699]/30 via-purple-300 to-[#E62020]/30" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-16 sm:mt-24 lg:mt-32" />
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const portfolioItems = [
    {
      title: 'AI Tools',
      status: 'Coming Soon',
      icon: Zap,
      bgColor: 'bg-[#002699]/5',
      borderColor: 'border-[#002699]/20',
      hoverBg: 'hover:bg-[#002699]/10',
    },
    {
      title: 'Trading & Finance',
      status: 'Coming Soon',
      icon: TrendingUp,
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      hoverBg: 'hover:bg-purple-100',
    },
    {
      title: 'Digital Media',
      status: 'Coming Soon',
      icon: Globe,
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      hoverBg: 'hover:bg-pink-100',
    },
    {
      title: 'Commerce Brands',
      status: 'Coming Soon',
      icon: BarChart3,
      bgColor: 'bg-[#E62020]/5',
      borderColor: 'border-[#E62020]/20',
      hoverBg: 'hover:bg-[#E62020]/10',
    },
  ];

  return (
    <section 
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
            <span className="gradient-text">Portfolio</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto px-2">
            Our ventures in progress. Building the future, one experiment at a time.
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {portfolioItems.map((item, index) => (
            <div
              key={item.title}
              className={`card-hover rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 ${item.bgColor} border ${item.borderColor} ${item.hoverBg} cursor-pointer group transition-all duration-300`}
              data-testid={`portfolio-card-${index}`}
            >
              <div className="flex flex-col h-full">
                <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl bg-white shadow-sm flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <item.icon className="w-5 sm:w-6 h-5 sm:h-6 text-gray-700" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold mb-1 sm:mb-2 text-gray-900">{item.title}</h3>
                <span className="text-xs sm:text-sm text-[#002699] font-medium flex items-center gap-1 sm:gap-2 mt-auto">
                  {item.status}
                  <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-16 sm:mt-24 lg:mt-32" />
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about"
      data-testid="about-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-gray-50 bg-grid overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Section Header */}
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-8 sm:mb-12 text-gray-900">
            About <span className="gradient-text">Xerobrain Labs</span>
          </h2>

          {/* About Text */}
          <div className="gradient-border rounded-xl sm:rounded-2xl p-6 sm:p-10 lg:p-14 bg-white hover:shadow-xl transition-shadow duration-500">
            <p className="text-base sm:text-xl lg:text-2xl text-gray-700 leading-relaxed" data-testid="about-text">
              Xerobrain Labs is an AI-native venture studio focused on building intelligent 
              systems that can operate, decide, and scale in the digital world. We believe 
              AI will power the next generation of global businesses.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 sm:mt-16">
            {[
              { label: 'AI-First', value: '100%' },
              { label: 'Ventures', value: '4+' },
              { label: 'Vision', value: '2026' },
            ].map((stat, index) => (
              <div key={stat.label} className="text-center group cursor-default">
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold gradient-text mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-16 sm:mt-24 lg:mt-32" />
    </section>
  );
};

// Contact Section (on homepage)
const ContactSectionHome = () => {
  return (
    <section 
      id="contact-section"
      data-testid="contact-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          {/* Section Header */}
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
            Get in <span className="gradient-text">Touch</span>
          </h2>

          <p className="text-base sm:text-xl text-gray-600 mb-8 sm:mb-12" data-testid="contact-text">
            Interested in collaborating or learning more?
          </p>

          {/* Email */}
          <a
            href="mailto:priyanshu@xerobrain.com"
            className="inline-flex items-center gap-3 sm:gap-4 px-4 sm:px-8 py-4 sm:py-5 rounded-xl sm:rounded-2xl gradient-border bg-white group card-hover hover:shadow-xl transition-all duration-300"
            data-testid="contact-email"
          >
            <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#002699] to-[#E62020] flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 flex-shrink-0">
              <Mail className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-xs sm:text-sm text-gray-500 mb-0.5 sm:mb-1">Email us at</div>
              <div className="text-sm sm:text-lg font-medium text-gray-900 group-hover:text-[#002699] transition-colors truncate">
                priyanshu@xerobrain.com
              </div>
            </div>
            <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 text-gray-400 group-hover:text-[#002699] group-hover:translate-x-2 transition-all duration-300 ml-2 sm:ml-4 flex-shrink-0" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <WhatWeBuildSection />
      <OurModelSection />
      <PortfolioSection />
      <AboutSection />
      <ContactSectionHome />
      <Footer />
    </>
  );
};

// Layout wrapper for sub-pages
const PageLayout = ({ children }) => {
  return (
    <>
      {children}
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <div className="min-h-screen bg-white text-gray-900" data-testid="app-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PageLayout><Privacy /></PageLayout>} />
          <Route path="/terms" element={<PageLayout><Terms /></PageLayout>} />
          <Route path="/contact" element={<PageLayout><Contact /></PageLayout>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
